import { type MedplumClient, type QueryTypes, resolveId } from "@medplum/core";
import type { Basic } from "@medplum/fhirtypes";
import { findIdentifier } from "@vibrant/models/fhir/fhir-resource-utils";
import {
  CAL_EXTENSION_URL_ACCESS_TOKEN_EXPIRES_AT,
  CAL_EXTENSION_URL_PREVIOUS_ACCESS_TOKEN_SET_AT,
  CAL_EXTENSION_URL_REFRESH_TOKEN,
  CAL_IDENTIFIER_SYSTEM_ACCESS_TOKENS,
  CAL_IDENTIFIER_SYSTEM_PREVIOUS_ACCESS_TOKENS,
  CAL_SYSTEM,
} from "@/lib/cal/client";
import { isMedplumVersionConflictError } from "@/lib/errors/is-medplum-version-conflict-error";
import {
  type ExtensionPatchRequest,
  findExtension,
  getExtensionPatchOperations,
  getIdentifierPatchOperations,
  type IdentifierPatchRequest,
} from "../utils";

export type SerializedCalCredentials = Basic;

export interface CalCredentialsCreateQuery {
  providerId?: string;
  memberId?: string;
  roomId?: string;
  accessToken: string;
  accessTokenExpiresAt: number;
  refreshToken: string;
}

export interface CalCredentialsSearchQuery {
  providerId?: string;
  memberId?: string;
  roomId?: string;
  accessToken?: string;
  /** Match against the one-behind token stored during concurrent refresh races */
  previousAccessToken?: string;
}

/** FHIR `Basic.code` token that identifies a Cal credentials record. */
export const CAL_CREDENTIALS_CODE = `${CAL_SYSTEM}|credentials`;

interface PatchCalCredentialsOptions {
  /**
   * If provided, only apply patch mutations while this token is still current.
   * Helps avoid rotating away freshly-minted tokens during concurrent refresh races.
   */
  expectedCurrentAccessToken?: string;
}

export class CalCredentials {
  constructor(readonly resource: Basic) {}

  private getSubjectId(
    resourcePrefix: "Practitioner" | "Patient" | "Location",
  ): string | undefined {
    const ref = this.resource.subject?.reference;
    if (ref?.startsWith(`${resourcePrefix}/`)) {
      return resolveId(this.resource.subject);
    }
    return undefined;
  }

  get id(): string {
    if (!this.resource.id) {
      throw new Error("Cal credentials missing id");
    }
    return this.resource.id;
  }

  get providerId(): string | undefined {
    return this.getSubjectId("Practitioner");
  }

  get memberId(): string | undefined {
    return this.getSubjectId("Patient");
  }

  get roomId(): string | undefined {
    return this.getSubjectId("Location");
  }

  get accessToken(): string {
    const identifier = findIdentifier(this.resource, CAL_IDENTIFIER_SYSTEM_ACCESS_TOKENS);
    return identifier?.value ?? "";
  }

  get accessTokenExpiresAt(): number {
    const extension = findExtension(this.resource, CAL_EXTENSION_URL_ACCESS_TOKEN_EXPIRES_AT);
    return extension?.valueDecimal ?? 0;
  }

  get refreshToken(): string {
    const extension = findExtension(this.resource, CAL_EXTENSION_URL_REFRESH_TOKEN);
    return extension?.valueString ?? "";
  }

  get previousAccessTokenSetAt(): number | undefined {
    const extension = findExtension(this.resource, CAL_EXTENSION_URL_PREVIOUS_ACCESS_TOKEN_SET_AT);
    return extension?.valueDecimal;
  }

  serialize(): Basic {
    return this.resource;
  }

  static async delete(medplum: MedplumClient, id: string): Promise<void> {
    const resource = await medplum.readResource("Basic", id);
    assertCalCredentialsResource({ id, resource });
    await medplum.deleteResource("Basic", id);
  }
}

/**
 * Protects credential lookups from unrelated Basic resources that share the same subject.
 */
function isCalCredentialsResource({ resource }: { resource: Basic }): boolean {
  return (
    resource.code?.coding?.some(
      (coding) => coding.system === CAL_SYSTEM && coding.code === "credentials",
    ) ?? false
  );
}

/**
 * Prevents Cal writes from mutating unrelated Basic resources.
 */
function assertCalCredentialsResource({ id, resource }: { id: string; resource: Basic }): void {
  if (isCalCredentialsResource({ resource })) {
    return;
  }

  throw new Error(`Basic/${id} is not Cal credentials`);
}

export async function createCalCredentials(
  medplum: MedplumClient,
  createQuery: CalCredentialsCreateQuery,
): Promise<CalCredentials> {
  const subjectCount = [createQuery.providerId, createQuery.memberId, createQuery.roomId].filter(
    Boolean,
  ).length;

  if (subjectCount !== 1) {
    throw new Error("Exactly one of providerId, memberId, or roomId must be provided");
  }

  // Determine the subject reference based on which ID was provided
  let subjectReference: string;
  if (createQuery.providerId) {
    subjectReference = `Practitioner/${createQuery.providerId}`;
  } else if (createQuery.memberId) {
    subjectReference = `Patient/${createQuery.memberId}`;
  } else {
    subjectReference = `Location/${createQuery.roomId}`;
  }

  const resource = await medplum.createResource({
    resourceType: "Basic",
    code: {
      coding: [
        {
          system: CAL_SYSTEM,
          code: "credentials",
        },
      ],
    },
    subject: {
      reference: subjectReference,
    },
    identifier: [
      {
        system: CAL_IDENTIFIER_SYSTEM_ACCESS_TOKENS,
        value: createQuery.accessToken,
      },
    ],
    extension: [
      {
        url: CAL_EXTENSION_URL_ACCESS_TOKEN_EXPIRES_AT,
        valueDecimal: createQuery.accessTokenExpiresAt,
      },
      {
        url: CAL_EXTENSION_URL_REFRESH_TOKEN,
        valueString: createQuery.refreshToken,
      },
    ],
  });
  return new CalCredentials(resource);
}

export async function searchOneCalCredentials(
  medplum: MedplumClient,
  searchQuery: CalCredentialsSearchQuery,
): Promise<CalCredentials | undefined> {
  // if searchquery has no keys, return null
  if (Object.keys(searchQuery).length === 0) {
    return undefined;
  }

  const subjectCount = [searchQuery.providerId, searchQuery.memberId, searchQuery.roomId].filter(
    Boolean,
  ).length;

  if (subjectCount > 1) {
    throw new Error("Only one of providerId, memberId, or roomId can be provided");
  }

  const queryTypes: QueryTypes = {};
  queryTypes.code = CAL_CREDENTIALS_CODE;

  if (searchQuery.providerId) {
    queryTypes.subject = `Practitioner/${searchQuery.providerId}`;
  } else if (searchQuery.memberId) {
    queryTypes.subject = `Patient/${searchQuery.memberId}`;
  } else if (searchQuery.roomId) {
    queryTypes.subject = `Location/${searchQuery.roomId}`;
  }

  if (searchQuery.accessToken) {
    queryTypes.identifier = `${CAL_IDENTIFIER_SYSTEM_ACCESS_TOKENS}|${searchQuery.accessToken}`;
  } else if (searchQuery.previousAccessToken) {
    queryTypes.identifier = `${CAL_IDENTIFIER_SYSTEM_PREVIOUS_ACCESS_TOKENS}|${searchQuery.previousAccessToken}`;
  }

  const credentials = await medplum.searchOne("Basic", queryTypes);
  if (!credentials || !isCalCredentialsResource({ resource: credentials })) {
    return undefined;
  }

  return new CalCredentials(credentials);
}

function hasPatchBeenApplied({
  credentials,
  props,
}: {
  credentials: CalCredentials;
  props: Partial<CalCredentialsCreateQuery>;
}): boolean {
  if (props.accessToken !== undefined && credentials.accessToken !== props.accessToken) {
    return false;
  }

  if (props.refreshToken !== undefined && credentials.refreshToken !== props.refreshToken) {
    return false;
  }

  if (
    props.accessTokenExpiresAt !== undefined &&
    credentials.accessTokenExpiresAt !== props.accessTokenExpiresAt
  ) {
    return false;
  }

  return true;
}

interface PatchCalCredentialsParams {
  id: string;
  props: Partial<CalCredentialsCreateQuery>;
  options?: PatchCalCredentialsOptions;
}

export async function patchCalCredentials(
  medplum: MedplumClient,
  { id, props, options }: PatchCalCredentialsParams,
): Promise<CalCredentials> {
  const MAX_ATTEMPTS = 3;
  let current = await medplum.readResource("Basic", id);
  assertCalCredentialsResource({ id, resource: current });
  let lastError: unknown;

  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt += 1) {
    if (
      options?.expectedCurrentAccessToken !== undefined &&
      new CalCredentials(current).accessToken !== options.expectedCurrentAccessToken
    ) {
      return new CalCredentials(current);
    }

    const versionId = current.meta?.versionId;
    if (!versionId) {
      throw new Error("Cannot patch Cal credentials: Basic missing meta.versionId");
    }

    const identifierPatchRequests: IdentifierPatchRequest[] = [];
    const extensionPatchRequests: ExtensionPatchRequest[] = [];

    if (props.accessToken !== undefined) {
      // Preserve the outgoing token before replacing the current token. This keeps
      // one-behind lookup stable and race-safe during concurrent refresh calls.
      const outgoingToken = findIdentifier(current, CAL_IDENTIFIER_SYSTEM_ACCESS_TOKENS)?.value;
      if (outgoingToken && outgoingToken !== props.accessToken) {
        identifierPatchRequests.push({
          resource: current,
          system: CAL_IDENTIFIER_SYSTEM_PREVIOUS_ACCESS_TOKENS,
          value: outgoingToken,
        });
        extensionPatchRequests.push({
          resource: current,
          url: CAL_EXTENSION_URL_PREVIOUS_ACCESS_TOKEN_SET_AT,
          value: Date.now(),
        });
      }

      identifierPatchRequests.push({
        resource: current,
        system: CAL_IDENTIFIER_SYSTEM_ACCESS_TOKENS,
        value: props.accessToken,
      });
    }

    if (props.refreshToken !== undefined) {
      extensionPatchRequests.push({
        resource: current,
        url: CAL_EXTENSION_URL_REFRESH_TOKEN,
        value: props.refreshToken,
      });
    }

    if (props.accessTokenExpiresAt !== undefined) {
      extensionPatchRequests.push({
        resource: current,
        url: CAL_EXTENSION_URL_ACCESS_TOKEN_EXPIRES_AT,
        value: props.accessTokenExpiresAt,
      });
    }

    const operations = [
      { op: "test" as const, path: "/meta/versionId", value: versionId },
      ...getIdentifierPatchOperations(current, identifierPatchRequests),
      ...getExtensionPatchOperations(current, extensionPatchRequests),
    ];

    if (operations.length === 1) {
      return new CalCredentials(current);
    }

    try {
      const updated = await medplum.patchResource("Basic", id, operations);
      return new CalCredentials(updated);
    } catch (error) {
      if (!isMedplumVersionConflictError(error)) {
        throw error;
      }

      lastError = error;
      const refreshed = await medplum.readResource("Basic", id);
      assertCalCredentialsResource({ id, resource: refreshed });
      const refreshedCredentials = new CalCredentials(refreshed);
      if (hasPatchBeenApplied({ credentials: refreshedCredentials, props })) {
        return refreshedCredentials;
      }
      if (
        options?.expectedCurrentAccessToken !== undefined &&
        refreshedCredentials.accessToken !== options.expectedCurrentAccessToken
      ) {
        return refreshedCredentials;
      }
      current = refreshed;
    }
  }

  throw lastError;
}
